import React, { useCallback, useEffect, useMemo } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { ProviderSelector } from '@shipstation/connections-ui';
import { DirectCarrierList } from '@shipstation/connections-ui/direct';
import axios from 'axios';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { noop } from 'ui-components/default-props/noop';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { Button } from 'ui-components/components/button';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { ProviderTypes } from './constants';
import { Providers } from '../enums/providers';
import { useActiveSellerFulfillmentProviders } from '../hooks/use-seller-fulfillment-providers';
import { useSellerLabelProviders } from '../hooks/use-seller-label-providers';
import Global from '../store/global-selectors';
import { useCuiApiConfig } from './helpers/use-cui-api-config';
import { useCuiContextProps } from './helpers/use-cui-context-props';
import { getInternalIdFromApiCode } from './helpers/get-internal-id-from-api-code';
import { useCommonCuiProps } from './helpers/use-common-cui-props';
import { useHideStampsConnection } from '../feature-flags/hooks/use-enable-stamps-link';
import { useEnableDirectModeCarriers } from '../feature-flags/hooks/use-enable-direct-mode-carriers';
import getDisplayInformation from '../components/data-driven-logo/get-display-information';
import { useNonWalletProviderFeatureGate } from '../feature-gates/use-non-wallet-provider-feature-gate';
import { useShouldShowNewLineupByoaUi } from '../hooks/non-wallet-access-hooks';
import { useGlobalModalLaunchContext } from '../contexts/modal-context/global-modal-context';
import { PromotionalBanner } from '../components/promotional-banner';

const stampsProviderIds = [Providers.Stamps.labelProviderId, Providers.ClientStamps.labelProviderId];

const messages = defineMessages({
  lockedCarrierTooltip: {
    defaultMessage: 'Upgrade to connect this carrier',
    description: 'Tooltip shown on carrier tiles that are locked on the current plan',
    id: 'YyA4+a',
  },
  planBannerIncludedCarriers: {
    description: 'Connect carrier modal plan banner — number of included carrier integrations',
    defaultMessage: 'Your plan includes {count} carrier {count, plural, one {integration} other {integrations}}.',
    id: 'CPvGlf',
  },
  planBannerUpgradePrompt: {
    description: 'Connect carrier modal plan banner — upgrade prompt',
    defaultMessage: 'Upgrade and get access to 50+ carriers.',
    id: 'zB11g5',
  },
  planBannerUpgradeButton: {
    description: 'Connect carrier modal plan banner — upgrade button label',
    defaultMessage: 'Upgrade',
    id: 'PuHSq/',
  },
});

export const CuiProviderSelector = ({
  onProviderSelected = noop,
  onRegisterSuccess = noop,
  onRegisterFailure = noop,
  onBack = noop,
  onCancel = noop,
  onError = noop,
  providerType = ProviderTypes.shipping,
}) => {
  const intl = useIntl();
  const isDirectMode = useEnableDirectModeCarriers();
  const hideStampsConnection = useHideStampsConnection();
  const commonCuiProps = useCommonCuiProps();
  const cuiApiConfig = useCuiApiConfig();
  const cuiContextProps = useCuiContextProps();
  const userIsSysAdmin = useSelector(Global.selectUserisSysAdmin);
  const { isFeatureEnabled } = useNonWalletProviderFeatureGate();
  const shouldShowNewLineupByoaUi = useShouldShowNewLineupByoaUi();
  const shouldLockDisallowedProviders = !isFeatureEnabled && shouldShowNewLineupByoaUi;
  const { openManageSubscriptionDrawer } = useGlobalModalLaunchContext();
  const [loading, setLoading] = useAsyncSafeState(true);
  const [providerList, setProviderList] = useAsyncSafeState([]);
  const [alwaysAllowedCount, setAlwaysAllowedCount] = useAsyncSafeState(0);
  const sellerProviders = useSellerLabelProviders();
  const sellerFulfillmentProviders = useActiveSellerFulfillmentProviders();
  const onProviderSelectedWithId = useCallback(
    (provider) => {
      onProviderSelected({
        ...provider,
        providerId: getInternalIdFromApiCode(provider.providerApiCode, provider.providerType),
      });
    },
    [onProviderSelected],
  );

  useEffect(() => {
    axios
      .get('/provider/availableProviders')
      .then((response) => {
        let providers = providerType === ProviderTypes.shipping ? response.data.providers : response.data.fulfillmentProviders;
        providers = providers.map((provider) => {
          const providerCollection = providerType === ProviderTypes.shipping ? sellerProviders : sellerFulfillmentProviders;
          const idProp = providerType === ProviderTypes.shipping ? 'labelProviderId' : 'fulfillmentProviderId';
          const matchingProvider = providerCollection.find((p) => p[idProp] === provider.providerId && !p.requiresSetup);
          return {
            ...provider,
            alreadyConnected: matchingProvider !== undefined,
            providerApiCode: provider.apiCode,
            providerType,
            isAlwaysAllowed: getDisplayInformation({ providerId: provider.providerId })?.isAlwaysAllowed,
          };
        });

        let providersToDisplay =
          hideStampsConnection && providerType === ProviderTypes.shipping
            ? providers.filter((provider) => !stampsProviderIds.includes(provider.providerId))
            : providers;

        if (shouldLockDisallowedProviders) {
          providersToDisplay = [...providersToDisplay].sort((a, b) => Number(b.isAlwaysAllowed) - Number(a.isAlwaysAllowed));
        }

        setProviderList(providersToDisplay);
        setAlwaysAllowedCount(providersToDisplay.filter((p) => p.isAlwaysAllowed).length);
      })
      .catch((error) => {
        onError(error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    setLoading,
    setProviderList,
    setAlwaysAllowedCount,
    sellerProviders,
    sellerFulfillmentProviders,
    userIsSysAdmin,
    onError,
    providerType,
    hideStampsConnection,
    shouldLockDisallowedProviders,
  ]);

  const directContextProps = useMemo(
    () => ({
      ...cuiContextProps,
      providerList,
      showSeeServices: false,
      onProviderSelected: onProviderSelectedWithId,
      onRegisterSuccess,
      onRegisterFailure,
      onRegisterSubmitRedirectForm: onRegisterSuccess, // used by canada post
      onBack,
      onCancel,
      onError,
      shouldLockDisallowedProviders,
      lockedTooltip: intl.formatMessage(messages.lockedCarrierTooltip),
      gridBanner: shouldLockDisallowedProviders ? (
        <PromotionalBanner
          promotionalHeaderMessage={<FormattedMessage {...messages.planBannerIncludedCarriers} values={{ count: alwaysAllowedCount }} />}
          promotionalSubheaderMessage={messages.planBannerUpgradePrompt}
        >
          <Button onClick={openManageSubscriptionDrawer} theme={THEME_CONSTANTS.promotional}>
            <FormattedMessage {...messages.planBannerUpgradeButton} />
          </Button>
        </PromotionalBanner>
      ) : null,
    }),
    [
      cuiContextProps,
      providerList,
      onProviderSelectedWithId,
      onRegisterSuccess,
      onRegisterFailure,
      onBack,
      onCancel,
      onError,
      shouldLockDisallowedProviders,
      alwaysAllowedCount,
      openManageSubscriptionDrawer,
      intl,
    ],
  );

  if (loading) {
    return <LoadingIndicator />;
  }

  if (isDirectMode) {
    return <DirectCarrierList apiConfig={cuiApiConfig} contextProps={directContextProps} />;
  }

  return (
    <ProviderSelector {...commonCuiProps} onError={onError} onProviderSelected={onProviderSelectedWithId} providerList={providerList} />
  );
};

CuiProviderSelector.propTypes = {
  onProviderSelected: PropTypes.func,
  onRegisterSuccess: PropTypes.func,
  onRegisterFailure: PropTypes.func,
  onBack: PropTypes.func,
  onCancel: PropTypes.func,
  onError: PropTypes.func,
  providerType: PropTypes.oneOf(Object.values(ProviderTypes)),
};
